import React, { useState, useEffect, useMemo } from 'react'
import PostData from '../../services/PostData';
import PutData from '../../services/PutData';
import GetData from '../../services/GetData';
import CloseIcon from '@mui/icons-material/Close';
import {
    TextField,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Box,
    IconButton,
    Typography, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
    Autocomplete, Tooltip,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import SnackbarEx from '../Helpers/SnackbarEx'
import PostPrintLabel from '../../services/PostPrintLabel';
import { openPdfInNewTab } from '../../services/openPrintTab.js';
import SplitOrderDialog from './SplitOrderDialog';
import AddVariantDialog from './AddVariantDialog';

function catalogOptionLabel(item) {
    const parts = [item?.itemId || ""];
    if (item?.macSubId != null && item.macSubId !== "") {
        parts.push(`MAC ${item.macSubId}`);
    }
    if (item?.status) parts.push(item.status);
    if (item?.platform) parts.push(item.platform);
    return parts.filter(Boolean).join(" · ");
}

const WEIGHT_TOOLTIP = 'Round Up to Nearest Pound';
const LENGTH_TOOLTIP = "Measure along Box's Tape, Round Up to Nearest Inch";
const WIDTH_TOOLTIP = "Measure against Box's Tape, Round Up to Nearest Inch";
const HEIGHT_TOOLTIP = 'Measure dimension without opening, Round Up to Nearest Inch';

const SHIPPING_FIELD_KEYS = [
    'Address 1',
    'Ship to city',
    'Ship to province/region/state',
    'Ship to zip',
    'Ship to country',
];

/** Default ship-from — used on UPS / Walmart / eBay labels unless overridden */
const DEFAULT_SHIP_FROM = {
    name: 'Broyles Ventures, LLC',
    company: 'No. 3 Retailers',
    address: '322 W 32nd St #B04',
    city: 'Charlotte',
    state: 'NC',
    zip: '28206',
    country: 'US',
};

const SHIP_FROM_FIELD_KEYS = ['name', 'company', 'address', 'city', 'state', 'zip', 'country'];

const cloneShipFrom = (src = DEFAULT_SHIP_FROM) =>
    SHIP_FROM_FIELD_KEYS.reduce((acc, key) => {
        acc[key] = src[key] ?? '';
        return acc;
    }, {});

const getShippingFromRecord = (rec) => {
    const fields = Array.isArray(rec) ? (rec[0]?.fields ?? {}) : (rec?.fields ?? {});
    return SHIPPING_FIELD_KEYS.reduce((acc, key) => {
        const value = fields[key];
        if (Array.isArray(value)) {
            acc[key] = value.filter(Boolean).join('\n');
        } else {
            acc[key] = value != null ? String(value) : '';
        }
        return acc;
    }, {});
};

const getBaseOrderRecord = (rec) => {
    if (Array.isArray(rec) && rec[0]?.fields) return rec[0];
    if (rec?.fields) return rec;
    return null;
};

const PACK_TYPES = ['Retail', 'Single', 'Double', '3x', '4x', 'Single BIAB', 'Double BIAB', '3x BIAB', '4x BIAB'];
const DIMENSIONS = [
    { key: 'Weight', label: 'Weight', unit: ' lbs' },
    { key: 'Length', label: 'Length', unit: ' in' },
    { key: 'Width', label: 'Width', unit: ' in' },
    { key: 'Height', label: 'Height', unit: ' in' },
];

const isValidMetricValue = (value) =>
    value != null && value !== '' && !(typeof value === 'object' && value?.error);

const buildPackageInfoFromMetrics = (metrics, packType) => {
    const next = { height: '', width: '', length: '', weight: '' };
    if (!metrics || !packType) return next;

    for (const { key } of DIMENSIONS) {
        const metricKey = `${packType} - ${key}`;
        const value = metrics[metricKey];
        if (isValidMetricValue(value)) {
            next[key.toLowerCase()] = String(value);
        }
    }
    return next;
};

export default function PrintOrder({ record, close, isSplit, onLinked }) {
    const [showAddressWarning, setShowAddressWarning] = useState(false);
    const [rates, setRates] = useState([]);
    const [walmartAlert, setWalmartAlert] = useState(null);
    const [ebayAlert, setEbayAlert] = useState(null);
    const [selectedRate, setSelectedRate] = useState('');
    const [openSplitDialog, setOpenSplitDialog] = useState(false);
    const [editingShipping, setEditingShipping] = useState(false);
    const [shippingFields, setShippingFields] = useState(() => getShippingFromRecord(record));
    const [draftShipping, setDraftShipping] = useState(() => getShippingFromRecord(record));
    const [editingShipFrom, setEditingShipFrom] = useState(false);
    const [shipFromFields, setShipFromFields] = useState(() => cloneShipFrom());
    const [draftShipFrom, setDraftShipFrom] = useState(() => cloneShipFrom());
    const [shipFromExpanded, setShipFromExpanded] = useState(false);
    const [shipToExpanded, setShipToExpanded] = useState(false);

    const [packageInfo, setPackageInfo] = useState({
        height: '',
        width: '',
        length: '',
        weight: '',
    })
    const [labelOptions, setLabelOptions] = useState([]);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [packType, setPackType] = useState('');
    const [boxConfigurations, setBoxConfigurations] = useState([]);
    const [selectedBoxConfigKey, setSelectedBoxConfigKey] = useState('');
    const [loadingSkuMetrics, setLoadingSkuMetrics] = useState(false);
    const [openAddVariant, setOpenAddVariant] = useState(false);
    const [variantDialogMode, setVariantDialogMode] = useState('create');
    const [variantRecordId, setVariantRecordId] = useState(null);
    const [cloneFromRecordId, setCloneFromRecordId] = useState(null);
    const [skuMetricsChecked, setSkuMetricsChecked] = useState(false);
    const [catalogItems, setCatalogItems] = useState([]);
    const [catalogSearchHits, setCatalogSearchHits] = useState([]);
    const [catalogQuery, setCatalogQuery] = useState("");
    const [searchingCatalog, setSearchingCatalog] = useState(false);
    const [linkedCatalogItems, setLinkedCatalogItems] = useState(() => {
        const rec = Array.isArray(record) ? record[0] : record;
        return Array.isArray(rec?.catalogItems) ? rec.catalogItems : [];
    });
    const [linkingCatalogItem, setLinkingCatalogItem] = useState(false);

    useEffect(() => {
        const rec = Array.isArray(record) ? record[0] : record;
        setLinkedCatalogItems(Array.isArray(rec?.catalogItems) ? rec.catalogItems : []);
    }, [record]);

    const [submitLoad, setSubmitLoad] = useState(false);
    const [gettingRates, setGettingRates] = useState(false);
    const [shippingCostDialog, setShippingCostDialog] = useState({ open: false, via: "" });
    const [manualShippingCost, setManualShippingCost] = useState("");
    const [manualTrackingNumber, setManualTrackingNumber] = useState("");
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false
    })

    const orderRecord = getBaseOrderRecord(record);
    const orderFields = orderRecord?.fields ?? {};
    const recordId = orderRecord?.id;

    useEffect(() => {
        const next = getShippingFromRecord(record);
        setShippingFields(next);
        setDraftShipping(next);
        setEditingShipping(false);
        setShipFromFields(cloneShipFrom());
        setDraftShipFrom(cloneShipFrom());
        setEditingShipFrom(false);
        setShipFromExpanded(false);
        setShipToExpanded(false);
    }, [recordId]);

    useEffect(() => {
        setShowAddressWarning(!shippingFields?.['Address 1']?.trim());
    }, [shippingFields]);

    useEffect(() => {
        setPackageInfo({ height: '', width: '', length: '', weight: '' });
        setLabelOptions([]);
        setSelectedLabel(null);
        setCatalogItems([]);
        setPackType('');
        setBoxConfigurations([]);
        setSelectedBoxConfigKey('');
        setRates([]);
        setSelectedRate('');
        setWalmartAlert(null);
        setEbayAlert(null);
        setSkuMetricsChecked(false);

        const rawItemSku = orderFields?.['SKU Helper'] ?? orderFields?.['Item ID'];
        const itemId = Array.isArray(rawItemSku) ? rawItemSku[0] : rawItemSku;
        if (!itemId || !recordId) {
            setSkuMetricsChecked(true);
            return;
        }

        const fetchSkuMetrics = async () => {
            setLoadingSkuMetrics(true);
            const { getData } = await GetData(
                `broylesllc/shipping/sku-metrics?itemSku=${encodeURIComponent(itemId)}`
            );
            setLoadingSkuMetrics(false);
            setSkuMetricsChecked(true);

            if (!getData?.found || !Array.isArray(getData.labels)) {
                setLabelOptions([]);
                setSelectedLabel(null);
            } else {
                setLabelOptions(getData.labels);
                setSelectedLabel(getData.labels[0] ?? null);
            }
            setPackType('');

            const catalog = await GetData(
                `broylesllc/catalog-items/by-walmart-sku?sku=${encodeURIComponent(itemId)}`
            );
            setCatalogItems(Array.isArray(catalog?.getData?.items) ? catalog.getData.items : []);
        };

        fetchSkuMetrics();
    }, [recordId, orderFields?.['SKU Helper'], orderFields?.['Item ID']]);

    const orderItemSku = useMemo(() => {
        const rawItemSku = orderFields?.['SKU Helper'] ?? orderFields?.['Item ID'];
        const itemId = Array.isArray(rawItemSku) ? rawItemSku[0] : rawItemSku;
        return itemId ? String(itemId) : '';
    }, [orderFields?.['SKU Helper'], orderFields?.['Item ID']]);

    useEffect(() => {
        const q = catalogQuery.trim();
        if (q.length < 2) {
            setCatalogSearchHits([]);
            return undefined;
        }
        const timer = setTimeout(async () => {
            setSearchingCatalog(true);
            const { getData } = await GetData(
                `broylesllc/catalog-items?q=${encodeURIComponent(q)}&pageSize=20`
            );
            setCatalogSearchHits(Array.isArray(getData?.items) ? getData.items : []);
            setSearchingCatalog(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [catalogQuery]);

    const catalogOptions = useMemo(() => {
        const merged = new Map();
        for (const item of [...linkedCatalogItems, ...catalogItems, ...catalogSearchHits]) {
            if (item?._id) merged.set(String(item._id), item);
        }
        return [...merged.values()];
    }, [linkedCatalogItems, catalogItems, catalogSearchHits]);

    const saveLinkedCatalogItems = (nextItems) => {
        const previous = linkedCatalogItems;
        setLinkedCatalogItems(nextItems);
        const shipmentId = Array.isArray(record) ? record[0]?.mongoId : record?.mongoId;
        if (!shipmentId) return;
        setLinkingCatalogItem(true);
        PutData(
            { catalogItemIds: nextItems.map((item) => item._id) },
            `broylesllc/shipments/${shipmentId}/catalog-item`
        ).then((result) => {
            setLinkingCatalogItem(false);
            if (result?.success) {
                onLinked?.();
                return;
            }
            setLinkedCatalogItems(previous);
            setOpenTestSnack({
                color: "error",
                message:
                    result?.error?.response?.data?.message ||
                    "Could not update catalog items on this shipment/order.",
                open: true,
            });
        });
    };

    // Scanner-friendly: auto-select on exact Item ID / Parser Item ID / MAC Sub ID match
    useEffect(() => {
        if (searchingCatalog || linkingCatalogItem) return;
        const q = catalogQuery.trim();
        if (q.length < 2 || !catalogSearchHits.length) return;

        const qLower = q.toLowerCase();
        const exactMatches = catalogSearchHits.filter((item) => {
            const keys = [
                item.itemId,
                item.parserItemId,
                item.macSubId != null && item.macSubId !== ""
                    ? String(item.macSubId)
                    : "",
            ];
            return keys.some(
                (key) => String(key || "").trim().toLowerCase() === qLower
            );
        });
        if (exactMatches.length !== 1) return;

        const match = exactMatches[0];
        const alreadySelected = linkedCatalogItems.some(
            (item) => String(item._id) === String(match._id)
        );
        setCatalogQuery("");
        setCatalogSearchHits([]);
        if (!alreadySelected) {
            saveLinkedCatalogItems([...linkedCatalogItems, match]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- saveLinkedCatalogItems is stable enough for scan UX
    }, [
        catalogSearchHits,
        searchingCatalog,
        linkingCatalogItem,
        catalogQuery,
        linkedCatalogItems,
    ]);

    const handleVariantSaved = (label, action = 'created') => {
        if (!label) return;
        setLabelOptions((prev) => {
            const without = prev.filter((l) => l.recordId !== label.recordId);
            return [...without, label].sort((a, b) =>
                String(a.label).localeCompare(String(b.label))
            );
        });
        setSelectedLabel(label);
        setPackType('');
        setOpenTestSnack({
            color: 'success',
            message: action === 'updated' ? 'Variant updated.' : 'Variant created.',
            open: true,
        });
    };

    const openCreateVariantDialog = () => {
        setVariantDialogMode('create');
        setVariantRecordId(null);
        setCloneFromRecordId(selectedLabel?.recordId ?? null);
        setOpenAddVariant(true);
    };

    const openEditVariantDialog = () => {
        if (!selectedLabel?.recordId) return;
        setVariantDialogMode('edit');
        setVariantRecordId(selectedLabel.recordId);
        setCloneFromRecordId(null);
        setOpenAddVariant(true);
    };

    const closeVariantDialog = () => {
        setOpenAddVariant(false);
        setVariantDialogMode('create');
        setVariantRecordId(null);
        setCloneFromRecordId(null);
    };

    useEffect(() => {
        if (!selectedLabel || !packType) {
            setBoxConfigurations([]);
            setSelectedBoxConfigKey('');
            return;
        }

        const configsForType = (selectedLabel.boxConfigurations ?? []).filter(
            (c) => c.label === packType
        );
        setBoxConfigurations(configsForType);
        setSelectedBoxConfigKey(configsForType.length === 1 ? configsForType[0].key : '');

        setPackageInfo(buildPackageInfoFromMetrics(selectedLabel.metrics, packType));
    }, [selectedLabel, packType]);

    const selectedTypeMetrics = useMemo(() => {
        if (!selectedLabel?.metrics || !packType) return null;
        const values = {};
        for (const { key, label, unit } of DIMENSIONS) {
            const metricKey = `${packType} - ${key}`;
            const value = selectedLabel.metrics[metricKey];
            if (isValidMetricValue(value)) {
                values[`${label}${unit}`] = value;
            }
        }
        return Object.keys(values).length > 0 ? values : null;
    }, [selectedLabel, packType]);

    const buildRecordWithShipping = () => {
        const base = getBaseOrderRecord(record);
        if (!base) return [];
        return [{
            ...base,
            fields: { ...base.fields, ...shippingFields },
        }];
    };

    const buildShipmentPayload = (extra = {}) => {
        const orderWithShipping = buildRecordWithShipping();
        return {
            0: orderWithShipping[0] ?? null,
            ...packageInfo,
            shipFrom: { ...shipFromFields },
            ...extra,
        };
    };

    const handleStartEditShipping = () => {
        setDraftShipping({ ...shippingFields });
        setEditingShipping(true);
        setShipToExpanded(true);
    };

    const handleCancelEditShipping = () => {
        setDraftShipping({ ...shippingFields });
        setEditingShipping(false);
    };

    const handleSaveShipping = () => {
        setShippingFields({ ...draftShipping });
        setEditingShipping(false);
        setRates([]);
        setSelectedRate('');
        setWalmartAlert(null);
        setEbayAlert(null);
    };

    const handleDraftShippingChange = (field) => (e) => {
        setDraftShipping((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleStartEditShipFrom = () => {
        setDraftShipFrom({ ...shipFromFields });
        setEditingShipFrom(true);
        setShipFromExpanded(true);
    };

    const handleCancelEditShipFrom = () => {
        setDraftShipFrom({ ...shipFromFields });
        setEditingShipFrom(false);
    };

    const handleSaveShipFrom = () => {
        setShipFromFields({ ...draftShipFrom });
        setEditingShipFrom(false);
        setRates([]);
        setSelectedRate('');
        setWalmartAlert(null);
        setEbayAlert(null);
    };

    const handleDraftShipFromChange = (field) => (e) => {
        setDraftShipFrom((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const shipFromIncomplete = !shipFromFields?.address?.trim()
        || !shipFromFields?.city?.trim()
        || !shipFromFields?.state?.trim()
        || !shipFromFields?.zip?.trim();

    const getRates = async () => {
        setGettingRates(true)
        setWalmartAlert(null)
        setEbayAlert(null)
        const rateData = buildShipmentPayload()
        const response = await PostData(rateData, "broylesllc/shipping/get-shipping-rates", null)
        if (response.success) {
            const payload = response.data;
            const rateList = Array.isArray(payload) ? payload : (payload?.rates ?? []);
            setRates(rateList);
            if (!Array.isArray(payload)) {
                if (payload?.walmartAlert) setWalmartAlert(payload.walmartAlert);
                if (payload?.ebayAlert) setEbayAlert(payload.ebayAlert);
            }
            setGettingRates(false)
        } else {
            setGettingRates(false)
        }
    }


    const openShippedThroughDialog = (via) => {
        const existingTracking =
            shippingFields?.["Tracking Number"]
            || orderFields?.["Tracking Number"]
            || orderFields?.["Shipping Tracking Number"]
            || "";
        setManualShippingCost("");
        setManualTrackingNumber(String(existingTracking || "").trim());
        setShippingCostDialog({ open: true, via });
    };

    const closeShippedThroughDialog = () => {
        if (submitLoad) return;
        setShippingCostDialog({ open: false, via: "" });
        setManualShippingCost("");
        setManualTrackingNumber("");
    };

    const markShippedThrough = async (via, labelCost, trackingNumber) => {
        const shipmentId = Array.isArray(record) ? record[0]?.mongoId : record?.mongoId;
        if (!shipmentId) {
            setOpenTestSnack({
                color: "error",
                message: "This shipment is not saved, so it cannot be marked shipped.",
                open: true,
            });
            return;
        }
        setSubmitLoad(true);
        const response = await PostData(
            { shippedVia: via, labelCost, trackingNumber },
            `broylesllc/shipments/${shipmentId}/shipped`,
            null
        );
        setSubmitLoad(false);
        if (response?.success) {
            setShippingCostDialog({ open: false, via: "" });
            setManualShippingCost("");
            setManualTrackingNumber("");
            close("success");
        } else {
            setOpenTestSnack({
                color: "error",
                message:
                    response?.error?.response?.data?.message
                    || `Could not mark shipped through ${via}.`,
                open: true,
            });
        }
    };

    const confirmShippedThrough = () => {
        const via = shippingCostDialog.via;
        const cost = Number(manualShippingCost);
        const trackingNumber = manualTrackingNumber.trim();
        if (manualShippingCost.trim() === "" || !Number.isFinite(cost) || cost < 0) {
            setOpenTestSnack({
                color: "warning",
                message: "Enter a valid shipping cost (0 or greater).",
                open: true,
            });
            return;
        }
        if (!trackingNumber) {
            setOpenTestSnack({
                color: "warning",
                message: "Enter a tracking number.",
                open: true,
            });
            return;
        }
        markShippedThrough(via, cost, trackingNumber);
    };

    const handleClose = () => {
        close()
    }
    // const handleRateChange = (event) => {
    //     console.log(event)
    //     const rateFound = rates.find(rate => rate.serviceName === event.target.value);
    //     setSelectedRate(rateFound);
    // };
    const handleRateChange = (event) => {
        const selectedUniqueValue = event.target.value; // e.g., "04W11J_Next Day Air_01"

        const foundRate = rates.find(rate =>
            `${rate.accountId}_${rate.serviceName}_${rate.code}` === selectedUniqueValue
        );
        setSelectedRate(foundRate);
    };

    const selectedBoxConfig = boxConfigurations.find((c) => c.key === selectedBoxConfigKey);
    const boxConfigRequired = boxConfigurations.length > 0;
    const canSubmit =
        !submitLoad &&
        !editingShipping &&
        !editingShipFrom &&
        !shipFromIncomplete &&
        shippingFields?.['Address 1']?.trim() &&
        selectedRate &&
        (!boxConfigRequired || selectedBoxConfigKey);

    const handleSubmit = async () => {
        if (boxConfigRequired && !selectedBoxConfigKey) {
            setOpenTestSnack({
                color: "warning",
                message: "Please select a box configuration.",
                open: true,
            });
            return;
        }

        setSubmitLoad(true)
        const submitData = buildShipmentPayload({
            ...selectedRate,
            boxConfigurationRecordId: selectedBoxConfig?.recordId ?? null,
        })
        const urlString = isSplit ? "split" : "regular"
        try {
            const response = await PostPrintLabel(submitData, `broylesllc/shipping/create-label/${urlString}`)
            if (response?.data && !response?.error) {
                if (!openPdfInNewTab(response.data)) {
                    setOpenTestSnack({
                        color: "warning",
                        message: "Allow popups for this site to print labels.",
                        open: true,
                    });
                    return;
                }
                const shipmentId = Array.isArray(record) ? record[0]?.mongoId : record?.mongoId;
                if (shipmentId) {
                    await PostData(
                        { shippedVia: "Bread Bskt" },
                        `broylesllc/shipments/${shipmentId}/shipped`,
                        null
                    );
                }
                close("success")
            } else {
                const errMsg = response.error?.message || "Error creating label";
                const retryHint = response.error?.retryable
                    ? " The label request timed out — wait a moment and try again."
                    : "";
                setOpenTestSnack({ color: "error", message: `${errMsg}${retryHint}`, open: true })
            }
        } finally {
            setSubmitLoad(false)
        }
    }
    return (
        <>
            <div className='card' style={{ height: '100%', border: 'none' }}>
                <div
                    style={{
                        height: 'calc(100vh - 20px)',
                        overflowY: 'auto', // Add scroll for long content
                        padding: '20px' // Add some padding
                    }}>
                    <div className='card-header'
                        style={{
                            backgroundColor: 'white', fontSize: '18px',
                            fontWeight: 'bold', border: 'none', zIndex: 1
                        }}>
                        {Array.isArray(orderFields?.["Shipping Label"]) &&
                        orderFields["Shipping Label"].length
                            ? "Edit Shipment"
                            : "Print Shipping Label"}
                    </div>


                    <div className='card-body'>
                        <Box sx={{ flexGrow: 1, p: 2 }}>
                            {/* Order Details Section */}
                            <Typography variant="h6" component="h6" gutterBottom>
                                Order Details
                            </Typography>
                            {showAddressWarning && (
                                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                                    ***Address 1 is required to ship.***
                                </Typography>
                            )}
                            <div> {/* Replaced Grid container */}
                                {orderFields?.["Order Number"] && (
                                    <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Order #:</strong>  <br />{orderFields?.["Order Number"]}</Typography></div>
                                )}
                                {orderFields?.["Item ID"] && (
                                    <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Item ID:</strong>  <br />{Array.isArray(orderFields["Item ID"]) ? orderFields["Item ID"][0] : orderFields["Item ID"]}</Typography></div>
                                )}
                                <Autocomplete
                                    multiple
                                    sx={{ mb: 1.5, mt: 1 }}
                                    size="small"
                                    options={catalogOptions}
                                    loading={linkingCatalogItem || searchingCatalog}
                                    value={linkedCatalogItems}
                                    inputValue={catalogQuery}
                                    onInputChange={(_event, value, reason) => {
                                        if (reason === "reset") return;
                                        setCatalogQuery(value);
                                    }}
                                    onChange={(_event, items) => saveLinkedCatalogItems(items)}
                                    getOptionLabel={(item) => catalogOptionLabel(item)}
                                    isOptionEqualToValue={(option, value) =>
                                        String(option._id) === String(value._id)
                                    }
                                    filterOptions={(options) => options}
                                    noOptionsText={
                                        catalogQuery.trim().length >= 2
                                            ? "No catalog items match that search."
                                            : orderItemSku
                                              ? `No automatic match for ${orderItemSku}. Search by item ID to add one.`
                                              : "Search by item ID"
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Catalog items"
                                            placeholder="Search item ID and add more than one"
                                            helperText="Changes update this shipment and the linked order."
                                        />
                                    )}
                                />
                                {orderFields?.["Buyer Name"] && (
                                    <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Customer:</strong>  <br />{orderFields?.["Buyer Name"]}</Typography></div>
                                )}
                                {orderFields?.["Item Title"] && (
                                    <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Item:</strong> <br />{orderFields?.["Item Title"]}</Typography></div>
                                )}
                                {orderFields?.["Quantity"] && (
                                    <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Quantity:</strong> <br />{orderFields?.["Quantity"]}</Typography></div>
                                )}
                                {orderFields?.["Item Subtotal"] && (
                                    <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Cost:</strong> ${Number(orderFields?.["Item Subtotal"]).toFixed(2)}</Typography></div>
                                )}
                            </div>

                            {/* Ship From */}
                            <Accordion
                                disableGutters
                                elevation={0}
                                expanded={shipFromExpanded}
                                onChange={(_, expanded) => setShipFromExpanded(expanded)}
                                sx={{
                                    mt: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px !important',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: 48,
                                        '& .MuiAccordionSummary-content': {
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            my: 1,
                                            mr: 1,
                                        },
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="h6" component="h6">
                                            Ship From
                                        </Typography>
                                        {!shipFromExpanded && (
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {[shipFromFields.address, shipFromFields.city, shipFromFields.state, shipFromFields.zip]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                    {!editingShipFrom && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<EditOutlinedIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStartEditShipFrom();
                                            }}
                                            disabled={editingShipping}
                                            sx={{ textTransform: 'none', flexShrink: 0, ml: 1 }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {shipFromIncomplete && !editingShipFrom && (
                                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                                            ***Ship From address, city, state, and zip are required.***
                                        </Typography>
                                    )}
                                    {editingShipFrom ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                <div style={{ flex: '1 1 160px' }}>
                                                    <TextField
                                                        label="Name"
                                                        value={draftShipFrom.name}
                                                        onChange={handleDraftShipFromChange('name')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 160px' }}>
                                                    <TextField
                                                        label="Company"
                                                        value={draftShipFrom.company}
                                                        onChange={handleDraftShipFromChange('company')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                            <TextField
                                                label="Address"
                                                value={draftShipFrom.address}
                                                onChange={handleDraftShipFromChange('address')}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                multiline
                                            />
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                <div style={{ flex: '1 1 140px' }}>
                                                    <TextField
                                                        label="City"
                                                        value={draftShipFrom.city}
                                                        onChange={handleDraftShipFromChange('city')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 100px' }}>
                                                    <TextField
                                                        label="State"
                                                        value={draftShipFrom.state}
                                                        onChange={handleDraftShipFromChange('state')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        helperText="Ex. NC"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 100px' }}>
                                                    <TextField
                                                        label="Zip"
                                                        value={draftShipFrom.zip}
                                                        onChange={handleDraftShipFromChange('zip')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 100px' }}>
                                                    <TextField
                                                        label="Country"
                                                        value={draftShipFrom.country}
                                                        onChange={handleDraftShipFromChange('country')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleSaveShipFrom}
                                                    disableElevation
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleCancelEditShipFrom}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </div>
                                    ) : (
                                        <div>
                                            {shipFromFields.name && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Name:</strong> <br />{shipFromFields.name}
                                                    </Typography>
                                                </div>
                                            )}
                                            {shipFromFields.company && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Company:</strong> <br />{shipFromFields.company}
                                                    </Typography>
                                                </div>
                                            )}
                                            {shipFromFields.address && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Address:</strong> <br />{shipFromFields.address}
                                                    </Typography>
                                                </div>
                                            )}
                                            {shipFromFields.city && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>City:</strong> <br />{shipFromFields.city}
                                                    </Typography>
                                                </div>
                                            )}
                                            {shipFromFields.state && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>State:</strong> <br />{shipFromFields.state}
                                                    </Typography>
                                                </div>
                                            )}
                                            {shipFromFields.zip && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Zip:</strong> <br />{shipFromFields.zip}
                                                    </Typography>
                                                </div>
                                            )}
                                            {shipFromFields.country && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Country:</strong> <br />{shipFromFields.country}
                                                    </Typography>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </AccordionDetails>
                            </Accordion>

                            {/* Ship To */}
                            <Accordion
                                disableGutters
                                elevation={0}
                                expanded={shipToExpanded}
                                onChange={(_, expanded) => setShipToExpanded(expanded)}
                                sx={{
                                    mt: 1.5,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px !important',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: 48,
                                        '& .MuiAccordionSummary-content': {
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            my: 1,
                                            mr: 1,
                                        },
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="h6" component="h6">
                                            Ship To
                                        </Typography>
                                        {!shipToExpanded && (
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {[
                                                    shippingFields['Address 1'],
                                                    shippingFields['Ship to city'],
                                                    shippingFields['Ship to province/region/state'],
                                                    shippingFields['Ship to zip'],
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                    {!editingShipping && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<EditOutlinedIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStartEditShipping();
                                            }}
                                            disabled={editingShipFrom}
                                            sx={{ textTransform: 'none', flexShrink: 0, ml: 1 }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {editingShipping ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' }}>
                                            <TextField
                                                label="Address"
                                                value={draftShipping['Address 1']}
                                                onChange={handleDraftShippingChange('Address 1')}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                multiline
                                            />
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                <div style={{ flex: '1 1 140px' }}>
                                                    <TextField
                                                        label="City"
                                                        value={draftShipping['Ship to city']}
                                                        onChange={handleDraftShippingChange('Ship to city')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 100px' }}>
                                                    <TextField
                                                        label="State"
                                                        value={draftShipping['Ship to province/region/state']}
                                                        onChange={handleDraftShippingChange('Ship to province/region/state')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        helperText="Ex. NC"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 100px' }}>
                                                    <TextField
                                                        label="Zip"
                                                        value={draftShipping['Ship to zip']}
                                                        onChange={handleDraftShippingChange('Ship to zip')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 100px' }}>
                                                    <TextField
                                                        label="Country"
                                                        value={draftShipping['Ship to country']}
                                                        onChange={handleDraftShippingChange('Ship to country')}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleSaveShipping}
                                                    disableElevation
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleCancelEditShipping}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </div>
                                    ) : (
                                        <div>
                                            {shippingFields['Address 1'] && (
                                                <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Address:</strong> <br /> {shippingFields['Address 1']}</Typography></div>
                                            )}
                                            {shippingFields['Ship to city'] && (
                                                <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>City:</strong> <br /> {shippingFields['Ship to city']}</Typography></div>
                                            )}
                                            {shippingFields['Ship to province/region/state'] && (
                                                <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>State: </strong> <br /> {shippingFields['Ship to province/region/state']}</Typography></div>
                                            )}
                                            {shippingFields['Ship to zip'] && (
                                                <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Zip: </strong> <br /> {shippingFields['Ship to zip']}</Typography></div>
                                            )}
                                            {shippingFields['Ship to country'] && (
                                                <div style={{ marginBottom: '4px' }}><Typography variant='subtitle2'><strong>Country:</strong> <br /> {shippingFields['Ship to country']}</Typography></div>
                                            )}
                                        </div>
                                    )}
                                </AccordionDetails>
                            </Accordion>

                            {(loadingSkuMetrics || skuMetricsChecked) && (
                                <Box sx={{ mt: 2 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="h6" component="h6">
                                            Pre-defined shipping metrics
                                        </Typography>
                                        {!loadingSkuMetrics && (
                                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    onClick={openCreateVariantDialog}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Add variant
                                                </Button>
                                                {selectedLabel && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<EditOutlinedIcon />}
                                                        onClick={openEditVariantDialog}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        Edit variant
                                                    </Button>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                    {loadingSkuMetrics ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Looking up SKU metrics...
                                        </Typography>
                                    ) : labelOptions.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            No pre-defined variant found for this SKU. Add one to save shipping metrics.
                                        </Typography>
                                    ) : (
                                        <>
                                            <Autocomplete
                                                value={selectedLabel}
                                                onChange={(_, newValue) => {
                                                    setSelectedLabel(newValue);
                                                    setPackType('');
                                                }}
                                                options={labelOptions}
                                                getOptionLabel={(option) => option?.label ?? ''}
                                                isOptionEqualToValue={(option, value) =>
                                                    option?.recordId === value?.recordId
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Product Variant"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                )}
                                                sx={{ mb: 2 }}
                                            />

                                            {selectedLabel && (
                                                <FormControl component="fieldset" size="small" sx={{ mb: 1 }}>
                                                    <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                                                        Pack type
                                                    </FormLabel>
                                                    <RadioGroup
                                                        row
                                                        sx={{ flexWrap: 'wrap' }}
                                                        aria-label="pack-type"
                                                        name="pack-type-group"
                                                        value={packType}
                                                        onChange={(e) => setPackType(e.target.value)}
                                                    >
                                                        {PACK_TYPES.map((type) => (
                                                            <FormControlLabel
                                                                key={type}
                                                                value={type}
                                                                control={<Radio size="small" />}
                                                                label={type}
                                                                sx={{
                                                                    mr: 2,
                                                                    '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                                                                }}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                            )}

                                            {selectedTypeMetrics && (
                                                <div style={{ marginBottom: '8px' }}>
                                                    {Object.entries(selectedTypeMetrics).map(([label, value]) => (
                                                        <div key={label} style={{ marginBottom: '4px' }}>
                                                            <Typography variant="subtitle2">
                                                                <strong>{label}:</strong> {value}
                                                            </Typography>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Box>
                            )}

                            {boxConfigurations.length > 0 && (
                                <FormControl component="fieldset" size="small" sx={{ mt: 2, mb: 1 }}>
                                    <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                                        Box configuration (select one)
                                    </FormLabel>
                                    <RadioGroup
                                        aria-label="box-configuration"
                                        name="box-configuration-group"
                                        value={selectedBoxConfigKey}
                                        onChange={(e) => setSelectedBoxConfigKey(e.target.value)}
                                    >
                                        {boxConfigurations.map((config) => (
                                            <FormControlLabel
                                                key={config.key}
                                                value={config.key}
                                                control={<Radio size="small" />}
                                                label={config.displayName || config.label}
                                                sx={{
                                                    marginBottom: 0,
                                                    '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                                                }}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            )}

                            <hr />

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                                <div style={{ flex: '1 1 200px' }}>
                                    <Tooltip title={WEIGHT_TOOLTIP} arrow placement="top">
                                        <TextField
                                            fullWidth
                                            label="Weight (lbs)"
                                            type="number"
                                            value={packageInfo.weight}
                                            onChange={(e) => {
                                                setPackageInfo(prevInfo => ({ ...prevInfo, weight: e.target.value }))
                                            }}
                                            variant="outlined"
                                            size="small"
                                            InputLabelProps={{ shrink: packageInfo.weight !== '' }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <Tooltip title={LENGTH_TOOLTIP} arrow placement="top">
                                        <TextField
                                            fullWidth
                                            label="Length (in)"
                                            type="number"
                                            value={packageInfo.length}
                                            onChange={(e) =>
                                                setPackageInfo(prevInfo => ({ ...prevInfo, length: e.target.value }))
                                            }
                                            variant="outlined"
                                            size="small"
                                            InputLabelProps={{ shrink: packageInfo.length !== '' }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">in</InputAdornment>,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <Tooltip title={WIDTH_TOOLTIP} arrow placement="top">
                                        <TextField
                                            fullWidth
                                            label="Width (in)"
                                            type="number"
                                            value={packageInfo.width}
                                            onChange={(e) =>
                                                setPackageInfo(prevInfo => ({ ...prevInfo, width: e.target.value }))
                                            }
                                            variant="outlined"
                                            size="small"
                                            InputLabelProps={{ shrink: packageInfo.width !== '' }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">in</InputAdornment>,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <Tooltip title={HEIGHT_TOOLTIP} arrow placement="top">
                                        <TextField
                                            fullWidth
                                            label="Height (in)"
                                            type="number"
                                            value={packageInfo.height}
                                            onChange={(e) =>
                                                setPackageInfo(prevInfo => ({ ...prevInfo, height: e.target.value }))
                                            }
                                            variant="outlined"
                                            size="small"
                                            InputLabelProps={{ shrink: packageInfo.height !== '' }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">in</InputAdornment>,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            </div>

                            {/* Get Rates Button */}
                            <div>
                                <Button
                                    loading={gettingRates}
                                    disabled={gettingRates || showAddressWarning || shipFromIncomplete || editingShipping || editingShipFrom}
                                    variant="contained"
                                    onClick={getRates}
                                    disableElevation
                                    sx={{ mt: 3, mr: 2 }}>
                                    Get Rates
                                </Button>
                                {!isSplit && <Button
                                    variant="outlined"
                                    onClick={() => setOpenSplitDialog(true)}
                                    startIcon={<CallSplitOutlinedIcon />}
                                    sx={{ mt: 3, textTransform: 'none' }}>
                                    Split
                                </Button>
                                }
                            </div>

                            {walmartAlert && (
                                <Typography color="warning.main" variant="body2" sx={{ mb: 0, mt: 1 }}>
                                    Walmart: {walmartAlert}
                                </Typography>
                            )}

                            {ebayAlert && (
                                <Typography color="warning.main" variant="body2" sx={{ mb: 0, mt: 1 }}>
                                    eBay: {ebayAlert}
                                </Typography>
                            )}

                            {rates.length > 0 && (
                                <FormControl component="fieldset" sx={{ mt: 3 }}>
                                    <FormLabel component="legend">Select a Shipping Rate</FormLabel>
                                    <RadioGroup
                                        aria-label="shipping-rates"
                                        name="shipping-rates-group"
                                        // value={selectedRate?.serviceName}
                                        value={selectedRate ? `${selectedRate.accountId}_${selectedRate.serviceName}_${selectedRate.code}` : ''}
                                        onChange={handleRateChange}
                                    >
                                        {rates.map((rate, index) => (
                                            <FormControlLabel
                                                key={`${rate.accountId}_${rate.serviceName}_${rate.code}`}
                                                value={`${rate.accountId}_${rate.serviceName}_${rate.code}`}
                                                control={<Radio />}
                                                label={`${rate.accountId}:${rate.serviceName} - $${Number(rate.price)?.toFixed(2)}`} // Assuming rate has price
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            )}
                        </Box>
                    </div>

                </div>

                <div>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute', width: '40px', height: '40px', top: '10px', right: '30px',
                            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                            backgroundColor: 'white', zIndex: 100, "&:hover": { backgroundColor: '#f0f0f0' }
                        }}
                    >
                        <CloseIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                </div>
                <div
                    className="card-footer bg-white d-flex justify-content-end"
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 100,
                        padding: '10px 20px',
                        marginTop: 'auto',
                    }}
                >
                    <Button
                        type="button"
                        variant="outlined"
                        disabled={submitLoad}
                        onClick={() => openShippedThroughDialog("eBay")}
                        sx={{ textTransform: "none", mr: 1, borderColor: "#fcd34d", color: "#854d0e" }}
                    >
                        Shipped through eBay
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        disabled={submitLoad}
                        onClick={() => openShippedThroughDialog("Walmart")}
                        sx={{ textTransform: "none", mr: 1, borderColor: "#93c5fd", color: "#1e40af" }}
                    >
                        Shipped through Walmart
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disableElevation
                        // color="primary"
                        sx={{ textTransform: 'none', minWidth: '200px', background: '#FF8C61' }}
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {submitLoad ? <CircularProgress size={20} color='white' /> : "Submit"}
                    </Button>
                </div>
            </div>

            {
                openTestSnack.open && <SnackbarEx color={openTestSnack.color} message={openTestSnack.message} open={openTestSnack.open}
                    close={() => setOpenTestSnack({
                        color: "",
                        message: "",
                        open: false
                    })} />
            }
            <Dialog
                maxWidth="xs"
                fullWidth
                open={shippingCostDialog.open}
                onClose={closeShippedThroughDialog}
            >
                <DialogTitle sx={{ padding: "16px 20px 8px" }}>
                    Marketplace shipment details
                </DialogTitle>
                <DialogContent sx={{ paddingTop: "8px !important" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        We can&apos;t pull shipping details from {shippingCostDialog.via || "marketplace"} orders.
                        Enter the tracking number and cost you paid so Shipping stays accurate.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Tracking number"
                        value={manualTrackingNumber}
                        disabled={submitLoad}
                        onChange={(e) => setManualTrackingNumber(e.target.value)}
                        sx={{ mb: 1.5 }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Shipping cost"
                        value={manualShippingCost}
                        disabled={submitLoad}
                        onChange={(e) => setManualShippingCost(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                confirmShippedThrough();
                            }
                        }}
                        inputProps={{ min: 0, step: "0.01" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button
                        onClick={closeShippedThroughDialog}
                        disabled={submitLoad}
                        sx={{ textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={
                            submitLoad
                            || manualShippingCost.trim() === ""
                            || manualTrackingNumber.trim() === ""
                        }
                        onClick={confirmShippedThrough}
                        sx={{ textTransform: "none", background: "#FF8C61" }}
                    >
                        {submitLoad ? <CircularProgress size={18} color="inherit" /> : `Mark shipped through ${shippingCostDialog.via || ""}`}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                maxWidth="sm"
                fullWidth
                open={openSplitDialog}
                onClose={() => setOpenSplitDialog(false)}
            >
                <SplitOrderDialog
                    record={record} // Pass the original record to the split dialog
                    close={() => setOpenSplitDialog(false)}
                />
            </Dialog>
            <Dialog
                maxWidth="md"
                fullWidth
                open={openAddVariant}
                onClose={closeVariantDialog}
            >
                <AddVariantDialog
                    key={`${variantDialogMode}-${variantRecordId ?? 'new'}-${cloneFromRecordId ?? 'none'}`}
                    itemSku={orderItemSku}
                    mode={variantDialogMode}
                    variantRecordId={variantRecordId}
                    cloneFromRecordId={cloneFromRecordId}
                    close={closeVariantDialog}
                    onSaved={handleVariantSaved}
                />
            </Dialog>
        </>
    );
};